import React, {useMemo, useState} from 'react';
import {renderToString} from 'react-dom/server';
import {Alert, Button} from 'antd';

import {jsPDF} from 'jspdf';
import {Canvg, presets} from 'canvg';

import {Diagnostic, RoiData, Summary} from 'routes/Dashboard/Diagnostic/Diagnostic.props';
import logo from './innovation_junkie_logo.jpeg';

import {QUESTION_TYPE} from 'types/Enum';
import {SurveyType} from 'models/Survey';
import {AssessmentChart} from 'components/AssessmentChart';
import * as R from 'ramda';
import html2canvas from 'html2canvas';
import {pdfFactory} from './pdf';
import {RowInput} from 'jspdf-autotable';


const toCurrency = (val: number): string => val.toLocaleString('en-US', {style: 'currency', currency: 'USD'});
const toPercentage = (x: number): string => `${x.toFixed(2)}%`;
const renderPercent = (r: any): string => `Low: ${toPercentage(r.low)} Avg: ${toPercentage(r.avg)} High: ${toPercentage(r.high)}`;
const formatName = (name: string): string => {
  let nameClone = name;

  nameClone = nameClone.replace('(left)', '');
  nameClone = nameClone.replace('(right)', '');

  return nameClone.trim();
}
const alignClass = (name: string) => name.includes('(left)') ? 'textLeft' : 'textRight';

const preset = presets.offscreen();

const svg2Blob = async ({width, height, svg}) => {
  const canvas = new OffscreenCanvas(width, height);
  const ctx = canvas.getContext('2d');
  const v = await Canvg.from(ctx, svg, preset);

  // Render only first frame, ignoring animations and mouse.
  await v.render();
  return await canvas.convertToBlob();
};

const toPng = async (blob: Blob) => {
  const pngUrl = URL.createObjectURL(blob);
  return pngUrl;
};

type Data = {
  name: string,
  low: number,
  avg: number,
  high: number
};

const renderSummaryPdf = async (
  title,
  companyName,
  summary,
  data,
  potentialImpact,
  annualRevenue,
  potentialRevenueImprovement,
  currentNetIncomeTable,
  desiredNetIncomeTable
) => {
  const format = [595, 842];
  const margin = 36;
  const doc = pdfFactory(title, format);

  const [width, height] = format;
  doc.addImage(logo, 'JPEG', margin, margin, 200, 70);

  const titleFontSize = 32;
  let x_pos = margin;
  let y_pos = 0;

  y_pos = (height - titleFontSize) / 2 + margin;
  doc.addTitle(title, y_pos, width, titleFontSize);
  doc.addSubTitle(companyName, {y: y_pos + 76});

  // heat chart
  doc.addPage(format, 'landscape');
  doc.addSubTitle('Diagnostic Heat Map', {y: margin});
  const svg = renderToString(
    <AssessmentChart
      data={
        Object.keys(summary).map(key => ({
          title: key,
          metric: summary[key].categoryScore
        }))
      }
    />
  );
  const imageData = await svg2Blob({
    width: height * 2,
    height: width * 2,
    svg
  }).then(toPng);
  doc.addImage(
    imageData,
    'PNG',
    0,
    13 + margin,
    height,
    width
  );

  doc.addPage(format, 'landscape');
  y_pos = margin;
  doc.addSubTitle("Potential Revenue Growth", {y: y_pos, width: height});
  let head = [{
    name: 'Category',
    low: 'Low',
    avg: 'Avg',
    high: 'High'
  }];

  y_pos += 24;
  doc.addTable(
    head,
    data.map((r, key) => ({
      name: r.name,
      low: toPercentage(r.low) + '\n' + toCurrency((r.low * annualRevenue) / 100),
      avg: toPercentage(r.avg) + '\n' + toCurrency((r.avg * annualRevenue) / 100),
      high: toPercentage(r.high) + '\n' + toCurrency((r.high * annualRevenue) / 100)
    })).concat([
      {
        name: potentialImpact.name,
        low: toCurrency(potentialImpact.low),
        avg: toCurrency(potentialImpact.avg),
        high: toCurrency(potentialImpact.high)
      }
    ]),
    {startY: y_pos}
  );

  y_pos = doc.lastAutoTable.finalY + margin;
  doc.addSubTitle('Potential Revenue Improvement', {y: y_pos, width: height});
  y_pos += 24;
  doc.addTable(
    head,
    potentialRevenueImprovement,
    {startY: y_pos}
  );

  doc.addPage(format, 'landscape');
  y_pos = margin;
  doc.addSubTitle('Potential Net Income Income Growth (Current Margin)', {y: y_pos, width: height});

  head = [
    {
      name: 'Financial Opportunity',
      low: 'Low',
      avg: 'Mean',
      high: 'High'
    }
  ];
  y_pos += 24;
  doc.addTable(
    head,
    currentNetIncomeTable,
    {startY: y_pos}
  );

  y_pos = doc.lastAutoTable.finalY + margin;
  doc.addSubTitle('Potential Net Income Income Growth (Benchmark Margin)', {y: y_pos, width: height});
  y_pos += 24;
  doc.addTable(
    head,
    desiredNetIncomeTable,
    {startY: y_pos}
  );

  return doc;
};

const renderDetailedPdf = async (
  title: string,
  companyName: string,
  summary: Summary,
  data: Data[],
  potentialImpact: Data,
  annualRevenue: number,
  potentialRevenueImprovement: RowInput[],
  currentNetIncomeTable: RowInput[],
  desiredNetIncomeTable: RowInput[],
  survey: SurveyType,
  getPercentageColor: (percent: number) => string
) => {
  const format = [595, 842];
  const margin = 36;
  const doc = pdfFactory(title, format);

  const [width, height] = format;
  doc.addImage(logo, 'JPEG', margin, margin, 200, 70);

  const titleFontSize = 32;
  let x_pos = margin;
  let y_pos = 0;

  y_pos = (height - titleFontSize) / 2 + margin;
  doc.addTitle(title, y_pos, width, titleFontSize);
  doc.addSubTitle(companyName, {y: y_pos + 76});

  // heat chart
  doc.addPage(format, 'landscape');
  doc.addSubTitle('Diagnostic Heat Map', {y: margin, width: height});
  const svg = renderToString(
    <AssessmentChart
      data={
        Object.keys(summary).map(key => ({
          title: key,
          metric: summary[key].categoryScore
        }))
      }
    />
  );
  let imageData = await svg2Blob({
    width: height * 2,
    height: width * 2,
    svg
  }).then(toPng);
  doc.addImage(
    imageData,
    'PNG',
    0,
    13 + margin,
    height,
    width
  );



  const noOpenEnded = (collection: Diagnostic[]) => collection.filter(({type}) => type !== QUESTION_TYPE.Input);
  survey?.order.map((k) => {
    doc.addPage(format, 'portrait');
    y_pos = margin;

    doc.addSubTitle(k, {y: y_pos, width, height});

    const toValue = ({
      favorable,
      totalResponses,
      type,
      avgNumericScore = 0,
    }) => {
      if (type == QUESTION_TYPE.Numeric) {
        return avgNumericScore;
      }
      return type == QUESTION_TYPE.Input ? ('Open Response') : ((favorable / totalResponses) * 100);
    };

    const body = noOpenEnded(summary[k]?.diagnostics || []).map((r) => ({
      question: r.question,
      type: r.type,
      response: toValue(r)
    }));

    y_pos += 13;
    doc.addTable(
      [{
        question: 'Best Practice',
        response: 'Favorable %'
      }],
      body,
      {
        startY: y_pos,
        didParseCell: function (data) {
          if (data.row.section === 'body' && data.column.dataKey === 'response') {
            data.cell.text = toPercentage(data.cell.raw);
          }
        },
        willDrawCell: function (data) {
          if (data.row.section === 'body' && data.column.dataKey === 'response') {
            const {x, y, raw: value} = data.cell;
          }
        },
        didDrawCell: function (data) {
          if (data.row.section === 'body' && data.column.dataKey === 'response') {
            const {
              x,
              y,
              width,
              height,
              raw: value,
              contentWidth,
              contentHeight
            } = data.cell;
            const center = {x: width / 2, y: height / 2};

            if (data.row.raw.type !== 'NUMERIC') {
              doc.setDrawColor(getPercentageColor(value));
              doc.setFillColor(getPercentageColor(value));
              // doc.setTextColor(getPercentageColor(value));
              doc.roundedRect(x + center.x - 33, y + center.y - 11, 66, 22, 5, 5, 'F');
              doc.setFont('OpenSans', 'bold');
              doc.text(
                toPercentage(value),
                x + center.x - 18,
                y + center.y + 4
              );
            } else {
              doc.roundedRect(x + center.x - 33, y + center.y - 11, 66, 22, 5, 5, 'S');
            }
            // doc.setTextColor('#000000');
          }
        }
      }
    );
  });

  doc.addPage(format, 'landscape');
  y_pos = margin;
  doc.addSubTitle("Potential Revenue Growth", {y: y_pos, width: height});
  let head = [{
    name: 'Category',
    low: 'Low',
    avg: 'Avg',
    high: 'High'
  }];

  y_pos += 24;
  doc.addTable(
    head,
    data.map((r, key) => ({
      name: r.name,
      low: toPercentage(r.low) + '\n' + toCurrency((r.low * annualRevenue) / 100),
      avg: toPercentage(r.avg) + '\n' + toCurrency((r.avg * annualRevenue) / 100),
      high: toPercentage(r.high) + '\n' + toCurrency((r.high * annualRevenue) / 100)
    })).concat([
      {
        name: potentialImpact.name,
        low: toCurrency(potentialImpact.low),
        avg: toCurrency(potentialImpact.avg),
        high: toCurrency(potentialImpact.high)
      }
    ]),
    {startY: y_pos}
  );

  y_pos = doc.lastAutoTable.finalY + margin;
  doc.addSubTitle('Potential Revenue Improvement', {y: y_pos, width: height});
  y_pos += 24;
  doc.addTable(
    head,
    potentialRevenueImprovement,
    {startY: y_pos}
  );

  doc.addPage(format, 'landscape');
  y_pos = margin;
  doc.addSubTitle('Potential Net Income Income Growth (Current Margin)', {y: y_pos, width: height});

  head = [
    {
      name: 'Financial Opportunity',
      low: 'Low',
      avg: 'Mean',
      high: 'High'
    }
  ];
  y_pos += 24;
  doc.addTable(
    head,
    currentNetIncomeTable,
    {startY: y_pos}
  );

  y_pos = doc.lastAutoTable.finalY + margin;
  doc.addSubTitle('Potential Net Income Income Growth (Benchmark Margin)', {y: y_pos, width: height});
  y_pos += 24;
  doc.addTable(
    head,
    desiredNetIncomeTable,
    {startY: y_pos}
  );


  const onlyOpenEnded = (collection: Diagnostic[]) => collection.filter(({type}) => type === QUESTION_TYPE.Input);
  doc.addPage(format, 'portrait');
  doc.addSubTitle('Open ended questions', {width, height});

  survey?.order.map((k) => {
    doc.addPage(format, 'portrait');
    y_pos = margin;

    console.log({title: k});
    doc.addSubTitle(k, {y: y_pos});

    const body = onlyOpenEnded(summary[k]?.diagnostics || []).map((r, key) => ({
      question: r.question,
      type: r.type,
      response: r.inputResponses.map(res => "•  " + res).join("\n")
    }));

    y_pos += 13;
    doc.addTable(
      [{
        question: 'Best Practice',
        response: 'Input Responses'
      }],
      body,
      {
        startY: y_pos,
        columnStyles: {
          question: {
            halign: 'left',
            valign: 'top',
            fontStyle: 'normal',
            textColor: [25, 66, 97],
            minCellWidth: 595 / 2,
          },
          response: {
            halign: 'left',
            valign: 'top',
            minCellWidth: 120,
            minCellHeight: 35
          }
        }
      }
    );
  });

  return doc;
};

export const GeneratePDF = (props: GeneratePDFProps): JSX.Element => {
  const [isRendering, setRendering] = useState(false);
  const [pdfPreviewData, setPdfPreviewData] = useState('');

  const data: Data[] = useMemo(() => Object.keys(props.impact).map(key => ({
    name: key,
    ...props.impact[key],
  })), [props.impact]);

  const potentialImpact: Data = useMemo(() => {
    let allAverages = 0;
    let allHigh = 0;
    let allLow = 0;
    let allAveragesPct = 0;
    let ndx = 0;

    data.forEach((d) => {
      allAveragesPct += d.avg;
      allAverages += (d.avg * props.companyAnnualRevenue) / 100;
      allHigh += (d.high * props.companyAnnualRevenue) / 100;
      allLow += (d.low * props.companyAnnualRevenue) / 100;
      ndx += 1;
    });

    // const averagePct = allAveragesPct / ndx;

    return {
      name: 'Potential Impact',
      high: allHigh,
      low: allLow,
      avg: allAverages,
      // averagePct: averagePct,
    };
  }, [data, props.companyAnnualRevenue]);

  const potentialRevenueImprovement = useMemo(() => {
    return [
      {
        name: 'Current Revenue',
        avg: toCurrency(props.companyAnnualRevenue),
        high: toCurrency(props.companyAnnualRevenue),
        low: toCurrency(props.companyAnnualRevenue),
      },
      {
        name: 'Potential New Revenue',
        avg: toCurrency(potentialImpact.avg + props.companyAnnualRevenue),
        high: toCurrency(potentialImpact.high + props.companyAnnualRevenue),
        low: toCurrency(potentialImpact.low + props.companyAnnualRevenue),
      },
      {
        name: 'Growth',
        avg: toPercentage((potentialImpact.avg / props.companyAnnualRevenue) * 100),
        high: toPercentage((potentialImpact.high / props.companyAnnualRevenue) * 100),
        low: toPercentage((potentialImpact.low / props.companyAnnualRevenue) * 100),
      },
    ];
  }, [potentialImpact, props.companyAnnualRevenue]);

  const currentNetIncomeTable = useMemo(() => {
    const currentNetIncome = toCurrency(props.currentNetIncome);
    const currentNetIncomeDecimal = props.currentNetIncomePct / 100;

    const avgNetIncome =
      (potentialImpact.avg + props.companyAnnualRevenue) *
      currentNetIncomeDecimal;
    const highNetIncome =
      (potentialImpact.high + props.companyAnnualRevenue) *
      currentNetIncomeDecimal;
    const lowNetIncome =
      (potentialImpact.low + props.companyAnnualRevenue) *
      currentNetIncomeDecimal;

    const avgDifference = avgNetIncome - props.currentNetIncome;
    const highDifference = highNetIncome - props.currentNetIncome;
    const lowDifference = lowNetIncome - props.currentNetIncome;

    const avgGrowth = (avgDifference / props.currentNetIncome) * 100;
    const highGrowth = (highDifference / props.currentNetIncome) * 100;
    const lowGrowth = (lowDifference / props.currentNetIncome) * 100;

    return [
      {
        name: 'Current Net Income',
        avg: currentNetIncome,
        high: currentNetIncome,
        low: currentNetIncome,
      },
      {
        name: `Potential New Net Income ${props.currentNetIncomePct}%`,
        avg: toCurrency(avgNetIncome),
        high: toCurrency(highNetIncome),
        low: toCurrency(lowNetIncome),
      },
      {
        name: 'Difference',
        avg: toCurrency(avgDifference),
        high: toCurrency(highDifference),
        low: toCurrency(lowDifference),
      },
      {
        name: 'Growth',
        avg: toPercentage(avgGrowth),
        high: toPercentage(highGrowth),
        low: toPercentage(lowGrowth),
      },
      {
        name: 'Five Year Profit Impact',
        avg: toCurrency(avgDifference * 5),
        high: toCurrency(highDifference * 5),
        low: toCurrency(lowDifference * 5),
      },
    ];
  }, [
    props.currentNetIncome,
    potentialImpact,
    props.currentNetIncomePct,
    props.companyAnnualRevenue,
  ]);

  const desiredNetIncomeTable = useMemo(() => {
    const desiredNetIncomeDecimal = props.desiredNetIncomePct / 100;

    const avgNetIncome =
      (potentialImpact.avg + props.companyAnnualRevenue) *
      desiredNetIncomeDecimal;
    const highNetIncome =
      (potentialImpact.high + props.companyAnnualRevenue) *
      desiredNetIncomeDecimal;
    const lowNetIncome =
      (potentialImpact.low + props.companyAnnualRevenue) *
      desiredNetIncomeDecimal;

    const avgDifference = avgNetIncome - props.currentNetIncome;
    const highDifference = highNetIncome - props.currentNetIncome;
    const lowDifference = lowNetIncome - props.currentNetIncome;

    const avgGrowth = (avgDifference / props.currentNetIncome) * 100;
    const highGrowth = (highDifference / props.currentNetIncome) * 100;
    const lowGrowth = (lowDifference / props.currentNetIncome) * 100;

    return [
      {
        name: 'Current Net Income',
        avg: toCurrency(props.currentNetIncome),
        high: toCurrency(props.currentNetIncome),
        low: toCurrency(props.currentNetIncome),
      },
      {
        name: `Potential New Net Income ${props.desiredNetIncomePct}%`,
        avg: toCurrency(avgNetIncome),
        high: toCurrency(highNetIncome),
        low: toCurrency(lowNetIncome),
      },
      {
        name: 'Difference',
        avg: toCurrency(avgDifference),
        high: toCurrency(highDifference),
        low: toCurrency(lowDifference),
      },
      {
        name: 'Growth',
        avg: toPercentage(avgGrowth),
        high: toPercentage(highGrowth),
        low: toPercentage(lowGrowth),
      },
      {
        name: 'Five Year Profit Impact',
        avg: toCurrency(avgDifference * 5),
        high: toCurrency(highDifference * 5),
        low: toCurrency(lowDifference * 5),
      },
    ];
  }, [
    potentialImpact,
    props.desiredNetIncomePct,
    props.companyAnnualRevenue,
    props.currentNetIncome,
  ]);

  const noOpenEnded = (collection: Diagnostic[]) => collection.filter(({type}) => type !== QUESTION_TYPE.Input);
  const onlyOpenEnded = (collection: Diagnostic[]) => collection.filter(({type}) => type === QUESTION_TYPE.Input);

  return (
    <>
      <Button
        type="primary"
        shape="round"
        disabled={isRendering}
        onClick={e => {
          setRendering(true);
          setPdfPreviewData('');

          renderSummaryPdf(
            "GrowthDX Summary Report",
            props.companyName,
            props.summary,
            data,
            potentialImpact,
            props.companyAnnualRevenue,
            potentialRevenueImprovement,
            currentNetIncomeTable,
            desiredNetIncomeTable
          ).then(doc => {
            if (process.env.NODE_ENV === 'development') {
              setPdfPreviewData(doc.output('datauristring'));
            } else {
              doc.save("GrowthDX " + props.companyName + " Summary Diagnostic.pdf");
            }
          }).finally(() => {
            setRendering(false);
          });
        }}
      >
        {isRendering ? "Loading document..." : "Download GrowthDX Summary Report"}
      </Button>
      &nbsp;
      <Button
        type="primary"
        shape="round"
        disabled={isRendering}
        onClick={e => {
          setRendering(true);
          setPdfPreviewData('');

          renderDetailedPdf(
            "GrowthDX Detailed Report",
            props.companyName,
            props.summary,
            data,
            potentialImpact,
            props.companyAnnualRevenue,
            potentialRevenueImprovement,
            currentNetIncomeTable,
            desiredNetIncomeTable,
            props.survey,
            props.getColorOfText
          ).then(doc => {
            if (process.env.NODE_ENV === 'development') {
              setPdfPreviewData(doc.output('datauristring'));
              // document.getElementById('preview')!.style.opacity = '1.0';
            } else {
              doc.save("GrowthDX " + props.companyName + " Full Diagnostic.pdf");
            }
          }).finally(() => {
            setRendering(false);
          });
        }}
      >
        {isRendering ? "Loading document..." : "Download GrowthDX Detailed Report"}
      </Button>
      {
        process.env.NODE_ENV === 'development' && pdfPreviewData &&
        <div id="preview" className="boxed">
          <embed width='100%' height='1200px' src={pdfPreviewData + '#page=1&view=FitH'} />
        </div>
      }
    </>
  );
}
